// src/lib/storage.ts (T1): per-viewer browser storage (spec 2.10: "hb:welcomed:<pid>" and the like).
// Every read and write is guarded: storage can be missing, full or blocked (private windows, Safari ITP,
// embedded webviews), and the app must work the same without it. Keys are always prefixed "hb:".
import { effect, signal, type Signal } from "@preact/signals";

type Area = "local" | "session";
const PREFIX = "hb:";

function area(which: Area): Storage | null {
  try {
    const s = which === "local" ? window.localStorage : window.sessionStorage;
    return s ?? null;
  } catch {
    return null;
  }
}

function fullKey(key: string): string {
  return key.startsWith(PREFIX) ? key : PREFIX + key;
}

function makeStore(which: Area) {
  return {
    get(key: string): string | null {
      try {
        return area(which)?.getItem(fullKey(key)) ?? null;
      } catch {
        return null;
      }
    },
    set(key: string, value: string): void {
      try {
        area(which)?.setItem(fullKey(key), value);
      } catch {
        /* quota or blocked: the value simply is not remembered */
      }
    },
    remove(key: string): void {
      try {
        area(which)?.removeItem(fullKey(key));
      } catch {
        /* ignore */
      }
    },
    getJSON<T>(key: string, fallback: T): T {
      const raw = this.get(key);
      if (raw === null) return fallback;
      try {
        return JSON.parse(raw) as T;
      } catch {
        return fallback;
      }
    },
    setJSON(key: string, value: unknown): void {
      this.set(key, JSON.stringify(value));
    },
  };
}

/** localStorage: survives reloads and new tabs on this browser. */
export const local = makeStore("local");
/** sessionStorage: this tab only. */
export const session = makeStore("session");

/**
 * A signal backed by storage: it starts from the stored value (or `fallback`) and writes every change back.
 * `undefined` or `null` removes the key. Use for per-viewer UI state such as "welcome video dismissed".
 */
export function storedSignal<T>(key: string, fallback: T, which: Area = "local"): Signal<T> {
  const store = which === "local" ? local : session;
  const s = signal<T>(store.getJSON<T>(key, fallback));
  let first = true;
  effect(() => {
    const v = s.value;
    if (first) {
      first = false;
      return;
    }
    if (v === undefined || v === null) store.remove(key);
    else store.setJSON(key, v);
  });
  return s;
}
